//---React Libraries
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
//---ANT Design
import { Card, Col, Modal, Row, Select, Radio } from "antd";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  WarningOutlined,
  NotificationOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
//---Project Imports
import "../style.css";
import {
  fleetsUrl,
  imeiListApiUrl,
  vehicleAlertDetailsApiUrl,
  vehicleAlertGraphDetailsApiUrl,
} from "components/Common/endpoints";
import { getApis, postApis } from "components/Common/apis";
import DateTimePicker from "components/Common/DateTimePicker";
import DeviationMonitorTable from "./DeviationMonitorTable";
import FullGraphsModal from "./Panels/fullGraphsModal";
import { getGrafanaInterval } from "components/Common/Utils";
import { setPageLastUpdatedTime, setPageLoader } from "reducers/loaderSlice";

const DeviationMonitor = ({ selectedTab, vehicleDetails }) => {
  const dispatch = useDispatch();
  const [grafanaDetails, setGrafanaDetails] = useState({ open: false });
  const [dropdownDetails, setDropdownDetails] = useState({
    fleetList: [],
    imeiList: [],
    allImeiList: [],
  });
  const [filterDetails, setFilterDetails] = useState({
    fleet: null,
    imeiList: vehicleDetails ? [vehicleDetails?.vehImei] : ["all"],
    category: "",
    dateRange: [
      dayjs()
        .subtract(dayjs().get("date") - 1, "d")
        .set("hour", 0)
        .set("minute", 0)
        .set("second", 0),
      dayjs(),
    ],
    current: 1,
    pageSize: 10,
  });
  const [metaData, setMetaData] = useState({});
  const authToken = localStorage.getItem("authToken");

  const onFilterChange = (name, value) => {
    if (name === "fleet") {
      const imeiList =
        dropdownDetails?.fleetList.find((fleet) => fleet.fleetId === value)
          ?.vehicleImei || dropdownDetails?.allImeiList;
      setDropdownDetails((prev) => ({ ...prev, imeiList }));
      setFilterDetails((prev) => ({
        ...prev,
        [name]: value || null,
        imeiList: imeiList?.[0] ? ["all"] : [],
        current: 1,
      }));
    } else if (name === "imeiList") {
      const [firstImei, ...otherImei] = value;
      const selectedImei =
        !value?.length || otherImei.at(-1) === "all"
          ? ["all"]
          : firstImei === "all"
          ? otherImei
          : value;
      setFilterDetails((prev) => ({
        ...prev,
        imeiList: selectedImei,
        current: 1,
      }));
    } else {
      setFilterDetails((prev) => ({ ...prev, [name]: value, current: 1 }));
    }
  };

  const getImeiList = useCallback(async () => {
    await getApis(imeiListApiUrl, {}, authToken)
      .then((response) => {
        setDropdownDetails((prev) => ({
          ...prev,
          imeiList: response?.data || [],
          allImeiList: response?.data,
        }));
      })
      .catch((e) => toast.error(e.response?.data?.message));
  }, [authToken]);

  const getFleets = async () => {
    await getApis(fleetsUrl, {}, authToken)
      .then((response) => {
        setDropdownDetails((prev) => ({
          ...prev,
          fleetList: response?.data,
        }));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getVehicleAlertDetailsData = async () => {
    if (
      filterDetails?.fleet ||
      filterDetails?.imeiList?.length > 0 ||
      filterDetails?.imeiList?.includes("all")
    ) {
      const autoRefresh =
        dayjs(filterDetails?.dateRange?.[1]) > dayjs().subtract(1, "hour");
      const data = {
        fleetId: filterDetails.fleet,
        imeiList: filterDetails.imeiList.filter((item) => item !== "all"),
        category: filterDetails?.category,
        partName: filterDetails?.partName?.[0] || "",
        metric: filterDetails?.metric?.[0] || "",
        imei: filterDetails?.imei?.[0] || "",
        startTime: Math.floor(filterDetails?.dateRange?.[0]),
        endTime: autoRefresh
          ? Math.floor(dayjs())
          : Math.floor(filterDetails?.dateRange?.[1]),
        page: filterDetails.current - 1,
        size: filterDetails.pageSize,
      };
      await postApis(vehicleAlertDetailsApiUrl, data, authToken)
        .then((response) => {
          setMetaData(response?.data || {});
          dispatch(setPageLastUpdatedTime(Date.now()));
          dispatch(setPageLoader(autoRefresh ? "live" : null));
        })
        .catch((error) => {
          setMetaData({});
          console.log(error);
          dispatch(setPageLoader("live"));
        });
    }
  };

  useEffect(() => {
    if (selectedTab === "deviationMonitor") {
      getVehicleAlertDetailsData();
      dispatch(setPageLoader("refresh"));

      if (dayjs(filterDetails?.dateRange?.[1]) > dayjs().subtract(1, "hour")) {
        const intervalId = setInterval(() => {
          dispatch(setPageLoader("refresh"));
          getVehicleAlertDetailsData();
        }, 60 * 1000);

        return () => clearInterval(intervalId);
      }
    }

    // eslint-disable-next-line
  }, [filterDetails, selectedTab]);

  useEffect(() => {
    getImeiList();
    getFleets();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVehicleAlertGraphData = async (modalData) => {
    const data = {
      imei: modalData.imei,
      metric: modalData.metric,
      startTime: Math.floor(filterDetails?.dateRange?.[0]),
      endTime: Math.floor(filterDetails?.dateRange?.[1]),
      interval: getGrafanaInterval(
        filterDetails?.dateRange?.[0],
        filterDetails?.dateRange?.[1]
      ),
    };
    await getApis(vehicleAlertGraphDetailsApiUrl, data, authToken)
      .then((response) => {
        setGrafanaDetails((prev) => ({
          ...prev,
          timeSeries: [],
          boxPlotData: [],
          ...response?.data,
        }));
      })
      .catch((error) => {
        console.log(error);
        setGrafanaDetails((prev) => ({
          ...prev,
          timeSeries: [],
          boxPlotData: [],
        }));
      });
  };

  const handleOpen = (vehicleDetails) => {
    getVehicleAlertGraphData(vehicleDetails);
    setGrafanaDetails({ ...vehicleDetails, open: true, loading: true });
  };

  const handleCancel = () => {
    setGrafanaDetails({ open: false });
  };

  return (
    <Row className="deviation-monitor-page" data-testid="deviation-monitor">
      <Modal
        title={"Vehicle Alerts"}
        centered
        open={grafanaDetails?.open}
        onCancel={handleCancel}
        className="deviation-monitor-graph-modal"
        footer={null}
      >
        <FullGraphsModal
          grafanaDetails={grafanaDetails}
          chartList={["Alerts"]}
        />
      </Modal>
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Select
              allowClear
              showSearch
              value={filterDetails?.fleet}
              size="large"
              style={{ width: "100%" }}
              data-testid="alert-fleet-dropdown"
              onChange={(value) => onFilterChange("fleet", value)}
              options={[
                { label: "None", value: null },
                ...dropdownDetails?.fleetList?.map((i) => ({
                  label: i.fleetName,
                  value: i.fleetId,
                })),
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Select
              allowClear
              size="large"
              mode="multiple"
              placeholder="IMEI"
              data-testid="alert-imei-dropdown"
              className={
                "w100" +
                (!filterDetails?.imeiList?.includes("all")
                  ? " imei-values"
                  : "")
              }
              value={filterDetails?.imeiList}
              maxTagCount={"responsive"}
              status={
                filterDetails?.imeiList?.length === 1 &&
                !filterDetails?.imeiList.includes("all")
                  ? "error"
                  : null
              }
              onChange={(value) => onFilterChange("imeiList", value)}
            >
              {dropdownDetails?.imeiList?.length > 0 && (
                <Select.Option value="all">All</Select.Option>
              )}
              {dropdownDetails?.imeiList?.map((value, i) => (
                <Select.Option key={i} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={8} lg={12} xl={12} className="flex-end">
            <DateTimePicker
              dates={filterDetails?.dateRange}
              onChangePicker={(value) => onFilterChange("dateRange", value)}
              disableRange={1}
              disableRangeType={"M"}
              featureDate={true}
              extraFilter={true}
            />
          </Col>
        </Row>

        <Radio.Group
          onChange={(event) => onFilterChange("category", event.target.value)}
          value={filterDetails?.category}
          className="w100 mt1"
        >
          <Radio.Button value="" className="outliers">
            <Card bordered={false} className="h100 alert-status-card">
              <div className="w100 h100 text-center">
                <div className="alert-status-card-title flex-center">
                  All Outliers
                </div>
                <div className="count mt05">{metaData?.outliers}</div>
              </div>
            </Card>
          </Radio.Button>
          <Radio.Button value="critical" className="critical">
            <Card bordered={false} className="h100 alert-status-card">
              <div className="w100 h100 text-center">
                <div className="alert-status-card-title flex-center">
                  <WarningOutlined className="mr1 alert-status-icon" />
                  Critical Alerts
                </div>
                <div className="count mt05">{metaData?.critical}</div>
              </div>
            </Card>
          </Radio.Button>
          <Radio.Button value="warning" className="warning">
            <Card bordered={false} className="h100 alert-status-card">
              <div className="w100 h100 text-center">
                <div className="alert-status-card-title flex-center">
                  <NotificationOutlined className="mr1 alert-status-icon" />
                  Warning
                </div>
                <div className="count mt05">{metaData?.warning}</div>
              </div>
            </Card>
          </Radio.Button>
          <Radio.Button value="info" className="info">
            <Card bordered={false} className="h100 alert-status-card">
              <div className="w100 h100 text-center">
                <div className="alert-status-card-title flex-center">
                  <InfoCircleOutlined className="mr1 alert-status-icon" />
                  Informational
                </div>
                <div className="count mt05">{metaData?.informational}</div>
              </div>
            </Card>
          </Radio.Button>
        </Radio.Group>

        <Row gutter={[16, 16]} className="mt2">
          <Col span={24}>
            <DeviationMonitorTable
              filterDetails={filterDetails}
              setFilterDetails={setFilterDetails}
              metaData={metaData}
              handleOpen={handleOpen}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default DeviationMonitor;
